/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import { Button } from '@ui';
import { InlineEdit } from '@components/v2/InlineEdit';
import Select from '@components/v2/Select';
import TooltipText from '@components/v2/TooltipText';
import { translate } from '@utils/i18n';
import { TRANSLATION_LANGUAGES } from '@constants/translationLanguages';
import { FIELD_TYPES, NAMESPACE, removeField, updateField, parseOptionsString, generateKeyFromLabel, getFieldTypeRequirements } from './fieldDefinitionsUtils';
import { validateDefaultValue, hasUnpairedTranslatedOptions } from './fieldDefinitionsValidation';
import FieldDefinitionRow from './FieldDefinitionRow';
import './InlineFieldDefinitionsBuilderStyles.scss';
// Re-export validation utility for backward compatibility
export { validateFieldDefinitions as validateInlineFieldDefinitions } from './fieldDefinitionsValidation';
// Create a new empty field definition
const createNewField = () => ({
    key: `field_${Date.now()}`,
    label: '',
    type: 'text',
    required: false
});
export default function InlineFieldDefinitionsBuilder({ fieldDefinitions, onChange, hasExistingComponents = false, originalFieldKeys, translationLanguages = [] }) {
    // Track if user is adding a new field
    const [isAddingField, setIsAddingField] = useState(false);
    const [newField, setNewField] = useState(null);
    const handleAddField = () => {
        setNewField(createNewField());
        setIsAddingField(true);
    };
    const handleSaveNewField = (field) => {
        // Add the new field to the list and send to server
        onChange([...fieldDefinitions, field]);
        setIsAddingField(false);
        setNewField(null);
    };
    const handleCancelNewField = () => {
        setIsAddingField(false);
        setNewField(null);
    };
    const handleRemoveField = (index) => {
        onChange(removeField(fieldDefinitions, index));
    };
    const handleFieldChange = (index, updates) => {
        onChange(updateField(fieldDefinitions, index, updates));
    };
    return (React.createElement("div", { className: "inline-field-definitions-builder" },
        fieldDefinitions.map((field, index) => {
            const isExistingField = originalFieldKeys ? originalFieldKeys.has(field.key) : false;
            return (React.createElement(InlineFieldDefinitionRow, { translationLanguages: translationLanguages, key: field.key || index, field: field, onFieldChange: (updates) => handleFieldChange(index, updates), onRemove: () => handleRemoveField(index), hasExistingComponents: hasExistingComponents, isExistingField: isExistingField }));
        }),
        isAddingField && newField && (React.createElement(FieldDefinitionRow, { mode: "saveCancel", field: newField, onSave: handleSaveNewField, onCancel: handleCancelNewField, hasExistingComponents: hasExistingComponents, translationLanguages: translationLanguages })),
        !isAddingField && (React.createElement(Button, { variant: "highlighted", onClick: handleAddField }, translate('add_field', { namespace: NAMESPACE })))));
}
function InlineFieldDefinitionRow({ field, onFieldChange, onRemove, hasExistingComponents = false, isExistingField = false, translationLanguages = [] }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    const [defaultValueError, setDefaultValueError] = useState('');
    // Store original min/max values to enforce restrictions
    const [originalMin] = useState(field.min);
    const [originalMax] = useState(field.max);
    const [originalMaxLength] = useState(field.maxLength);
    const { needsOptions, needsMinMax, needsMaxLength } = getFieldTypeRequirements(field.type);
    // Determine if certain actions should be disabled
    const shouldApplyRestrictions = hasExistingComponents && isExistingField;
    // Deletion: Only prevent deleting fields that existed in the original schema
    const cannotDeleteField = hasExistingComponents && isExistingField;
    const cannotChangeType = shouldApplyRestrictions;
    // Required restriction applies for new fields when there are existing components
    const cannotMakeRequired = hasExistingComponents && !field.required;
    const handleLabelChange = (newLabel) => {
        // Only update if the label has actually changed
        if (newLabel !== field.label) {
            onFieldChange({ label: newLabel, key: field.key || generateKeyFromLabel(newLabel) });
        }
    };
    const handleTranslatedLabelChange = (key, value) => {
        if (value !== field[key]) {
            onFieldChange({ [key]: value || undefined });
        }
    };
    const handleTranslatedOptionsBlur = (key, optionsString) => {
        var _a;
        if (optionsString !== (((_a = field[key]) === null || _a === void 0 ? void 0 : _a.join(', ')) || '')) {
            onFieldChange({ [key]: optionsString ? parseOptionsString(optionsString) : undefined });
        }
    };
    const handleOptionsBlur = (optionsString) => {
        var _a;
        // Update field options immediately on blur
        const currentOptionsString = ((_a = field.options) === null || _a === void 0 ? void 0 : _a.join(', ')) || '';
        if (optionsString !== currentOptionsString) {
            const updates = { options: parseOptionsString(optionsString) };
            // Clear default value when options change
            if (field.type === 'select' || field.type === 'multiselect') {
                updates.default = undefined;
                setDefaultValueError('');
            }
            onFieldChange(updates);
        }
    };
    const handleMinChange = (value) => {
        const valueStr = typeof value === 'number' ? value.toString() : value;
        const newMin = valueStr ? Number(valueStr) : undefined;
        // Prevent increasing min value when there are existing components
        if (shouldApplyRestrictions && originalMin !== undefined && newMin !== undefined) {
            if (newMin > originalMin) {
                // Silently reject the change - the value will revert
                return;
            }
        }
        // Only update if the min value has actually changed
        if (newMin !== field.min) {
            onFieldChange({ min: newMin });
        }
    };
    const handleMaxChange = (value) => {
        const valueStr = typeof value === 'number' ? value.toString() : value;
        const newMax = valueStr ? Number(valueStr) : undefined;
        // Prevent decreasing max value when there are existing components
        if (shouldApplyRestrictions && originalMax !== undefined && newMax !== undefined) {
            if (newMax < originalMax) {
                // Silently reject the change - the value will revert
                return;
            }
        }
        // Only update if the max value has actually changed
        if (newMax !== field.max) {
            onFieldChange({ max: newMax });
        }
    };
    const handleMaxLengthChange = (value) => {
        const valueStr = typeof value === 'number' ? value.toString() : value;
        const newMaxLength = valueStr ? Number(valueStr) : undefined;
        // Prevent decreasing maxLength value when there are existing components
        if (shouldApplyRestrictions && originalMaxLength !== undefined && newMaxLength !== undefined) {
            if (newMaxLength < originalMaxLength) {
                // Silently reject the change - the value will revert
                return;
            }
        }
        // Only update if the maxLength value has actually changed
        if (newMaxLength !== field.maxLength) {
            onFieldChange({ maxLength: newMaxLength });
        }
    };
    const handleTypeChange = (newType) => {
        // Only update if the type has actually changed
        // The Select is constrained to FIELD_TYPES values, so this cast is safe
        if (newType !== field.type) {
            onFieldChange({ type: newType });
        }
    };
    const handleRequiredChange = (checked) => {
        // Prevent making optional fields required when there are existing components
        if (cannotMakeRequired && checked) {
            return;
        }
        // Only update if the required state has actually changed
        if (checked !== (field.required || false)) {
            onFieldChange({ required: checked });
        }
    };
    const getDefaultValue = () => {
        if (field.default === undefined || field.default === null)
            return '';
        if (Array.isArray(field.default))
            return field.default.join(', ');
        return field.default.toString();
    };
    const handleDefaultChange = (value) => {
        const currentDefault = getDefaultValue();
        if (value === currentDefault)
            return;
        if (!value) {
            onFieldChange({ default: undefined });
            return;
        }
        // Always update the value, don't block input
        if (field.type === 'number') {
            onFieldChange({ default: Number(value) });
        }
        else if (field.type === 'boolean') {
            onFieldChange({ default: value === 'true' });
        }
        else if (field.type === 'select') {
            onFieldChange({ default: value });
        }
        else if (field.type === 'multiselect') {
            onFieldChange({ default: parseOptionsString(value) });
        }
        else {
            onFieldChange({ default: value });
        }
    };
    const handleDefaultBlur = (value) => {
        // Validate on blur instead of on change
        const error = validateDefaultValue(value, field.type, field.options || []);
        setDefaultValueError(error);
    };
    const handleBooleanDefaultChange = (checked) => {
        if (checked !== field.default) {
            onFieldChange({ default: checked });
        }
    };
    const handleDateDefaultChange = (date) => {
        const newDefault = date ? date.toISOString() : undefined;
        if (newDefault !== field.default) {
            onFieldChange({ default: newDefault });
        }
    };
    const handleUnitChange = (value) => {
        if (value !== (field.unit || '')) {
            onFieldChange({ unit: value || undefined });
        }
    };
    return (React.createElement("div", { className: "inline-field-definition-row" },
        React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("label", { className: "inline-field-definition-row__label" },
                translate('field_label', { namespace: NAMESPACE }),
                ":"),
            React.createElement("div", { className: "inline-field-definition-row__value inline-field-definition-row__value--with-action" },
                React.createElement(InlineEdit, { as: "div", onBlur: (e) => {
                        const target = e.target;
                        handleLabelChange(target.textContent || '');
                    }, placeholder: translate('field_label_placeholder', { namespace: NAMESPACE }) }, field.label),
                cannotDeleteField ? (React.createElement(TooltipText, { text: translate('tooltips.cannot_delete_field', { namespace: NAMESPACE }) },
                    React.createElement(Button, { onClick: onRemove, "aria-label": translate('remove_field', { namespace: NAMESPACE }), disabled: true },
                        React.createElement("i", { className: "fa-light fa-trash-can" })))) : (React.createElement(Button, { onClick: onRemove, "aria-label": translate('remove_field', { namespace: NAMESPACE }) },
                    React.createElement("i", { className: "fa-light fa-trash-can" }))))),
        translationLanguages.includes(TRANSLATION_LANGUAGES.FR) && (React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("label", { className: "inline-field-definition-row__label" },
                translate('field_label_fr', { namespace: NAMESPACE }),
                ":"),
            React.createElement("div", { className: "inline-field-definition-row__value inline-field-definition-row__value--stretch" },
                React.createElement(InlineEdit, { as: "div", onBlur: (e) => {
                        const target = e.target;
                        handleTranslatedLabelChange('labelFr', target.textContent || '');
                    }, placeholder: translate('field_label_fr_placeholder', { namespace: NAMESPACE }) }, field.labelFr || '')))),
        translationLanguages.includes(TRANSLATION_LANGUAGES.ES) && (React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("label", { className: "inline-field-definition-row__label" },
                translate('field_label_es', { namespace: NAMESPACE }),
                ":"),
            React.createElement("div", { className: "inline-field-definition-row__value inline-field-definition-row__value--stretch" },
                React.createElement(InlineEdit, { as: "div", onBlur: (e) => {
                        const target = e.target;
                        handleTranslatedLabelChange('labelEs', target.textContent || '');
                    }, placeholder: translate('field_label_es_placeholder', { namespace: NAMESPACE }) }, field.labelEs || '')))),
        React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("label", { className: "inline-field-definition-row__label" },
                translate('field_type', { namespace: NAMESPACE }),
                ":"),
            React.createElement("div", { className: "inline-field-definition-row__value" }, cannotChangeType ? (React.createElement(TooltipText, { text: translate('tooltips.cannot_change_field_type', { namespace: NAMESPACE }) },
                React.createElement(Select, { className: "type-select-input", options: Array.from(FIELD_TYPES), value: field.type, onChange: handleTypeChange, disabled: true }))) : (React.createElement(Select, { className: "type-select-input", options: Array.from(FIELD_TYPES), value: field.type, onChange: handleTypeChange })))),
        needsOptions && (React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("label", { className: "inline-field-definition-row__label" }, translate('options_label', { namespace: NAMESPACE })),
            React.createElement("div", { className: "inline-field-definition-row__value inline-field-definition-row__value--full" },
                React.createElement(InlineEdit, { as: "div", onBlur: (e) => {
                        const target = e.target;
                        handleOptionsBlur(target.textContent || '');
                    }, placeholder: translate('options_placeholder', { namespace: NAMESPACE }) }, ((_a = field.options) === null || _a === void 0 ? void 0 : _a.join(', ')) || '')))),
        needsOptions && translationLanguages.includes(TRANSLATION_LANGUAGES.FR) && (React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("label", { className: "inline-field-definition-row__label" }, translate('options_label_fr', { namespace: NAMESPACE })),
            React.createElement("div", { className: "inline-field-definition-row__value inline-field-definition-row__value--full" },
                React.createElement(InlineEdit, { as: "div", onBlur: (e) => {
                        const target = e.target;
                        handleTranslatedOptionsBlur('optionsFr', target.textContent || '');
                    }, placeholder: translate('options_placeholder', { namespace: NAMESPACE }) }, ((_b = field.optionsFr) === null || _b === void 0 ? void 0 : _b.join(', ')) || '')))),
        needsOptions && translationLanguages.includes(TRANSLATION_LANGUAGES.ES) && (React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("label", { className: "inline-field-definition-row__label" }, translate('options_label_es', { namespace: NAMESPACE })),
            React.createElement("div", { className: "inline-field-definition-row__value inline-field-definition-row__value--full" },
                React.createElement(InlineEdit, { as: "div", onBlur: (e) => {
                        const target = e.target;
                        handleTranslatedOptionsBlur('optionsEs', target.textContent || '');
                    }, placeholder: translate('options_placeholder', { namespace: NAMESPACE }) }, ((_c = field.optionsEs) === null || _c === void 0 ? void 0 : _c.join(', ')) || '')))),
        needsOptions && hasUnpairedTranslatedOptions(field) && (React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("span", { className: "inline-field-definition-row__warning" },
                React.createElement("i", { className: "fa-light fa-triangle-exclamation" }),
                translate('unpaired_translated_options_warning', { namespace: NAMESPACE })))),
        needsMinMax && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "inline-field-definition-row__field-row" },
                React.createElement("label", { className: "inline-field-definition-row__label" },
                    translate('min', { namespace: NAMESPACE }),
                    ":"),
                React.createElement("div", { className: "inline-field-definition-row__value" }, shouldApplyRestrictions && originalMin !== undefined ? (React.createElement(TooltipText, { text: translate('tooltips.cannot_increase_min', { namespace: NAMESPACE }) },
                    React.createElement(InlineEdit, { as: "div", variant: "number", numberValue: ((_d = field.min) === null || _d === void 0 ? void 0 : _d.toString()) || '', onNumberChange: handleMinChange, placeholder: translate('min', { namespace: NAMESPACE }) }, ((_e = field.min) === null || _e === void 0 ? void 0 : _e.toString()) || ''))) : (React.createElement(InlineEdit, { as: "div", variant: "number", numberValue: ((_f = field.min) === null || _f === void 0 ? void 0 : _f.toString()) || '', onNumberChange: handleMinChange, placeholder: translate('min', { namespace: NAMESPACE }) }, ((_g = field.min) === null || _g === void 0 ? void 0 : _g.toString()) || '')))),
            React.createElement("div", { className: "inline-field-definition-row__field-row" },
                React.createElement("label", { className: "inline-field-definition-row__label" },
                    translate('max', { namespace: NAMESPACE }),
                    ":"),
                React.createElement("div", { className: "inline-field-definition-row__value" }, shouldApplyRestrictions && originalMax !== undefined ? (React.createElement(TooltipText, { text: translate('tooltips.cannot_decrease_max', { namespace: NAMESPACE }) },
                    React.createElement(InlineEdit, { as: "div", variant: "number", numberValue: ((_h = field.max) === null || _h === void 0 ? void 0 : _h.toString()) || '', onNumberChange: handleMaxChange, placeholder: translate('max', { namespace: NAMESPACE }) }, ((_j = field.max) === null || _j === void 0 ? void 0 : _j.toString()) || ''))) : (React.createElement(InlineEdit, { as: "div", variant: "number", numberValue: ((_k = field.max) === null || _k === void 0 ? void 0 : _k.toString()) || '', onNumberChange: handleMaxChange, placeholder: translate('max', { namespace: NAMESPACE }) }, ((_l = field.max) === null || _l === void 0 ? void 0 : _l.toString()) || '')))),
            React.createElement("div", { className: "inline-field-definition-row__field-row" },
                React.createElement("label", { className: "inline-field-definition-row__label" },
                    translate('unit', { namespace: NAMESPACE }),
                    ":"),
                React.createElement("div", { className: "inline-field-definition-row__value" },
                    React.createElement(InlineEdit, { as: "div", onBlur: (e) => {
                            const target = e.target;
                            handleUnitChange(target.textContent || '');
                        }, placeholder: translate('unit_placeholder', { namespace: NAMESPACE }) }, field.unit || ''))))),
        needsMaxLength && (React.createElement("div", { className: "inline-field-definition-row__field-row" },
            React.createElement("label", { className: "inline-field-definition-row__label" },
                translate('max_length', { namespace: NAMESPACE }),
                ":"),
            React.createElement("div", { className: "inline-field-definition-row__value" }, shouldApplyRestrictions && originalMaxLength !== undefined ? (React.createElement(TooltipText, { text: translate('tooltips.cannot_decrease_max_length', { namespace: NAMESPACE }) },
                React.createElement(InlineEdit, { as: "div", variant: "number", numberValue: ((_m = field.maxLength) === null || _m === void 0 ? void 0 : _m.toString()) || '', onNumberChange: handleMaxLengthChange, placeholder: translate('max_length', { namespace: NAMESPACE }) }, ((_o = field.maxLength) === null || _o === void 0 ? void 0 : _o.toString()) || ''))) : (React.createElement(InlineEdit, { as: "div", variant: "number", numberValue: ((_p = field.maxLength) === null || _p === void 0 ? void 0 : _p.toString()) || '', onNumberChange: handleMaxLengthChange, placeholder: translate('max_length', { namespace: NAMESPACE }) }, ((_q = field.maxLength) === null || _q === void 0 ? void 0 : _q.toString()) || ''))))),
        React.createElement("div", { className: "inline-field-definition-row__field-row" },
            field.type !== 'boolean' ? (React.createElement("label", { className: "inline-field-definition-row__label" },
                translate('default_value', { namespace: NAMESPACE }),
                ":")) : null,
            React.createElement("div", { className: "inline-field-definition-row__value" },
                field.type === 'boolean' && (React.createElement("div", { className: "qmb-checkbox" },
                    React.createElement("input", { type: "checkbox", id: `default-boolean-${field.key}`, checked: field.default === true, onChange: (e) => handleBooleanDefaultChange(e.target.checked) }),
                    React.createElement("label", { htmlFor: `default-boolean-${field.key}` }, translate('default_boolean_value', { namespace: NAMESPACE })))),
                field.type === 'date' && (React.createElement(InlineEdit, { variant: "date", className: "inline-field-definition-row__date-input", dateValue: field.default ? new Date(field.default) : undefined, onDateChange: (date) => handleDateDefaultChange(date), placeholder: translate('select_date_placeholder', { namespace: NAMESPACE }) }, field.default ? new Date(field.default).toLocaleDateString() : '')),
                field.type !== 'boolean' && field.type !== 'date' && (React.createElement(React.Fragment, null,
                    React.createElement(InlineEdit, { as: "div", variant: field.type === 'number' ? 'number' : undefined, numberValue: field.type === 'number' ? field.default : undefined, onNumberChange: field.type === 'number'
                            ? (val) => {
                                handleDefaultChange(val.toString());
                                handleDefaultBlur(val.toString());
                            }
                            : undefined, onBlur: field.type !== 'number'
                            ? (e) => {
                                const target = e.target;
                                const value = target.textContent || '';
                                handleDefaultChange(value);
                                handleDefaultBlur(value);
                            }
                            : undefined, placeholder: translate('default_value', { namespace: NAMESPACE }) }, getDefaultValue()),
                    defaultValueError && React.createElement("span", { className: "inline-field-definition-row__error" }, defaultValueError))))),
        React.createElement("div", { className: "inline-field-definition-row__checkbox" }, cannotMakeRequired ? (React.createElement(TooltipText, { text: translate('tooltips.cannot_make_required', { namespace: NAMESPACE }) },
            React.createElement("div", { className: "qmb-checkbox" },
                React.createElement("input", { type: "checkbox", id: `required-${field.key}`, checked: field.required || false, onChange: (e) => handleRequiredChange(e.target.checked), disabled: !field.required }),
                React.createElement("label", { htmlFor: `required-${field.key}` }, translate('required', { namespace: NAMESPACE }))))) : (React.createElement("div", { className: "qmb-checkbox" },
            React.createElement("input", { type: "checkbox", id: `required-${field.key}`, checked: field.required || false, onChange: (e) => handleRequiredChange(e.target.checked) }),
            React.createElement("label", { htmlFor: `required-${field.key}` }, translate('required', { namespace: NAMESPACE })))))));
}
